import {Inject, Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {County, Court, GeometryLookupResult} from '@ee/common/models';

@Injectable({ providedIn: 'root' })
export class CountyService {
  constructor(private http: HttpClient, @Inject('environment') private environment) {
  }

  getCountyById(id: string): Observable<County | undefined> {
    return this.http.get<County | undefined>(`${this.environment.api_prefix}api/counties/${id}`);
  }

  /**
   * A blank state is answered locally rather than sent. Interpolating an empty value produces
   * `api/counties/state/` with nothing after the slash, which matches no route — Spring falls through
   * to the static-resource handler and returns a 404 that reaches the user as a failed load. Callers
   * pass a state straight off an address that may not have one yet (an intake case mid-entry, for
   * one), so "no state" is an ordinary state of the world here, not a caller bug worth a round trip.
   */
  getCountiesByState(state: string): Observable<County[]> {
    if (!state?.trim()) {
      return of([]);
    }
    return this.http.get<County[]>(`${this.environment.api_prefix}api/counties/state/${encodeURIComponent(state.trim())}`);
  }

  getCountiesByZipCode(zip_code: string): Observable<County[]> {
    return this.http.get<County[]>(`${this.environment.api_prefix}api/counties/zip/${zip_code}`);
  }

  getGeometryLookup(lat: number, lng: number, state: string): Observable<GeometryLookupResult> {
    return this.http.get<GeometryLookupResult>(
      `${this.environment.api_prefix}api/counties/locate-by-coordinates`,
      { params: { lat: lat.toString(), lng: lng.toString(), state } }
    );
  }

  getGeometryMatch(lat: number, lng: number, state: string): Observable<County | null> {
    return this.http.get<County | null>(
      `${this.environment.api_prefix}api/counties/match-by-coordinates`,
      { params: { lat: lat.toString(), lng: lng.toString(), state } }
    );
  }

  updateCounty(county: County): Observable<County> {
    return this.http.put<County>(`${this.environment.api_prefix}api/counties/${county.id}`, county);
  }

  /**
   * Get available courts for a county (courts in the same state not already associated).
   */
  getAvailableCourtsForCounty(countyId: string): Observable<Court[]> {
    return this.http.get<Court[]>(`${this.environment.api_prefix}api/counties/${countyId}/available-courts`);
  }

  /**
   * Add a court reference to a county.
   */
  addCourtToCounty(countyId: string, courtId: string): Observable<County> {
    return this.http.post<County>(`${this.environment.api_prefix}api/counties/${countyId}/courts/${courtId}`, {});
  }

  /**
   * Remove a court reference from a county.
   */
  removeCourtFromCounty(countyId: string, courtId: string): Observable<County> {
    return this.http.delete<County>(`${this.environment.api_prefix}api/counties/${countyId}/courts/${courtId}`);
  }

  /**
   * Get a county by ID with courts populated.
   * Use this when you need the full court data for a county.
   */
  getCountyWithCourts(countyId: string): Observable<County> {
    return this.http.get<County>(`${this.environment.api_prefix}api/counties/${countyId}`);
  }
  /**
   * Get the recommended court for a county based on subdivision matching.
   * Returns the court matched by subdivision, or the default court, or null.
   */
  recommendCourt(countyId: string, subdivision: string): Observable<Court | null> {
    return this.http.get<Court | null>(
      `${this.environment.api_prefix}api/counties/${countyId}/recommend-court`,
      { params: { subdivision } }
    );
  }

  /**
   * Lookup the containing subdivision for a given lat/lng/state.
   * Returns the subdivision name or null.
   */
  lookupSubdivision(lat: number, lng: number, state: string): Observable<{ subdivision: string | null }> {
    return this.http.get<{ subdivision: string | null }>(
      `${this.environment.api_prefix}api/jurisdictional-boundaries/lookup`,
      { params: { lat: lat.toString(), lng: lng.toString(), state } }
    );
  }

}
